/**
 * Pure news/calendar rules.
 *
 * Kept dependency-free (no playwright, no express, no redis) so that every
 * consumer can share one copy of each rule, and so all of them are unit-testable
 * without standing the service up. The frontend store imports from here too, via
 * the `@news` alias.
 *
 * Anything in the news path that decides what to keep, what to send, or what to
 * re-broadcast belongs in this file. #375 and #377 were both caused by those
 * decisions being transcribed by hand at each call site and then drifting.
 */

/**
 * How long an event stays in the window after its release time.
 *
 * An event that released minutes ago still matters, and dropping it the instant
 * it passes makes the list flicker empty between releases.
 */
export const CALENDAR_LOOKBACK_MS = 12 * 60 * 60 * 1000;

/** Minimum shape `selectCalendarWindow` needs. */
export interface TimestampedEvent {
  timestamp: number;
}

export interface CalendarWindow<T extends TimestampedEvent> {
  /** The events to serve, capped at `limit`. */
  events: T[];
  /** How many cached events fell inside the lookback window, before the cap. */
  upcoming: number;
}

/**
 * Pick the slice of the calendar cache that is actually current.
 *
 * Anchor the window on NOW, not on index 0.
 *
 * The cache is sorted ASCENDING and accumulates across days, so `slice(0, limit)`
 * returned the OLDEST events — once more than `limit` had built up, today's
 * releases fell off the end and the panel showed nothing current. It degraded
 * silently as the cache grew, which is why it looked intermittent rather than
 * broken.
 *
 * THIS IS THE ONLY COPY OF THIS RULE. `GET /calendar` and the `news:init`
 * socket payload both call it. #375 happened because the fix landed in the REST
 * handler and the socket path kept its own `slice(0, 100)`.
 */
export function selectCalendarWindow<T extends TimestampedEvent>(
  events: T[],
  limit: number,
  now: number = Date.now()
): CalendarWindow<T> {
  const from = now - CALENDAR_LOOKBACK_MS;
  const relevant = events.filter(e => e.timestamp >= from);

  // Fall back to the tail rather than serving nothing if every cached event is
  // stale — an empty panel hides the staleness, the last known events do not.
  const window = relevant.length > 0 ? relevant : events.slice(-limit);

  return { events: window.slice(0, limit), upcoming: relevant.length };
}

/**
 * Whether a calendar row's `actual` column holds a real released value.
 *
 * Financial Juice renders a pending event's actual as a placeholder ("-"), and
 * `!!"-"` is `true`, so the old `isReleased: !!item.actual` flagged every
 * pending event as released.
 */
export function isActualReleased(actual: unknown): boolean {
  if (actual === undefined || actual === null) return false;
  const value = String(actual).trim().toLowerCase();
  if (value === '') return false;
  return !PLACEHOLDER_ACTUALS.has(value);
}

const PLACEHOLDER_ACTUALS = new Set(['-', '--', '---', '–', '—', 'n/a', 'na', 'tbd']);

/**
 * Trim an accumulating calendar CACHE down to `max`, keeping what still matters.
 *
 * Related to `selectCalendarWindow` but deliberately not the same rule:
 *
 * - `selectCalendarWindow` is a SEND rule. It always narrows to the lookback,
 *   because a payload should only carry what is current.
 * - `trimCalendarCache` is a STORAGE rule. Under the cap it returns the cache
 *   untouched, because a cache that is not overflowing has no reason to throw
 *   anything away — pruning early would drop this morning's releases out of the
 *   "today" panel by mid-afternoon.
 *
 * Both agree on the part that matters: when the cap does bite, drop from the
 * FAR END, never from the head. The cache is sorted ASCENDING and accumulates
 * across days, so `slice(0, max)` / `truncate(max)` keeps the OLDEST events and
 * silently drops today's — #375, which then reappeared in two more places
 * because the rule was written out by hand each time.
 *
 * MIRRORED IN RUST as `trim_calendar_events` in `utt-engine/src/news/mod.rs`.
 * The engine cannot import TypeScript, so those two are kept behaviourally
 * identical by hand — change both or they disagree. Every consumer that CAN
 * import this (news-service, and the frontend store via `@news/calendar`) must
 * call it rather than reimplement it. That is the whole point of #377.
 *
 * `now` is JS epoch MILLISECONDS, matching `TimestampedEvent.timestamp`.
 */
export function trimCalendarCache<T extends TimestampedEvent>(
  events: T[],
  max: number,
  now: number = Date.now()
): T[] {
  if (max <= 0) return [];
  if (events.length <= max) return events;

  const from = now - CALENDAR_LOOKBACK_MS;
  const relevant = events.filter(e => e.timestamp >= from);

  if (relevant.length === 0) {
    // Everything cached is stale. Keep the NEWEST `max` rather than the oldest —
    // an empty-looking panel hides the staleness, the last known events do not.
    return [...events].sort((a, b) => a.timestamp - b.timestamp).slice(-max);
  }

  // Soonest first, so overflowing on a heavy calendar day drops the far future
  // rather than the release that is about to print.
  return relevant.sort((a, b) => a.timestamp - b.timestamp).slice(0, max);
}

/**
 * Parse a `?limit=` query into 1..max, falling back to `fallback`.
 *
 * `parseInt(...) || fallback` alone lets negatives through — only 0 and NaN are
 * falsy — and every consumer of the result is a `slice`, where a negative length
 * means "count from the end", the exact opposite of a cap.
 */
export function clampLimit(raw: unknown, fallback: number, max: number): number {
  const parsed = parseInt(String(raw ?? ''), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.min(parsed, max);
}

/**
 * Whether a scraped value is a release time we can act on.
 *
 * Guards the two fabrications the scraper used to make. `new Date('garbage')`
 * does NOT throw, so the try/catch around it never fired and `.getTime()` put
 * `NaN` into the timestamp; elsewhere an unparsed row was simply stamped
 * `Date.now()`. Both reach the chart, the frontend's 10-minute proximity
 * warning, and the engine's red-folder news anchor as if they were real.
 */
export function isUsableTimestamp(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Whether a calendar row seen on a refresh should be broadcast.
 *
 * A released event has to stay re-emittable — its `actual` is the thing
 * subscribers are waiting for — but only when that value has actually MOVED.
 * Two earlier versions of this gate re-broadcast the entire calendar every
 * 5-minute refresh: `!!item.actual` was true for pending rows (they carry "-"),
 * and the fix for that still re-emitted unconditionally because its guard was
 * `seen.delete(id)` immediately followed by `seen.add(id)` — a no-op on a Set.
 */
export function shouldEmitCalendarRow(
  isReleased: boolean,
  actual: string,
  lastEmittedActual: string | undefined,
  alreadySeen: boolean
): boolean {
  if (isReleased) return lastEmittedActual !== actual;
  return !alreadySeen;
}

/**
 * How long BEFORE a red-folder release the no-trade window opens.
 *
 * The prop-firm rule itself is much tighter than this (Lucid: "flat from 1
 * minute before through 1 minute after"), but a window that opens one minute
 * early is useless as a warning — by the time it appears you are already in the
 * position you cannot hold. An hour is the amount of warning needed to decide
 * not to take the setup in the first place.
 */
export const NEWS_RESTRICTION_BEFORE_MS = 60 * 60 * 1000;

/**
 * How long AFTER the release the window stays open.
 *
 * The breach rule ends one minute after the print; five minutes covers the
 * revision/whipsaw that follows it, and stops the window from closing while
 * the spread is still blown out.
 */
export const NEWS_RESTRICTION_AFTER_MS = 5 * 60 * 1000;

/** A [start, end] interval, epoch MILLISECONDS. */
export interface RestrictionWindow {
  start: number;
  end: number;
}

/** Minimum shape `newsRestrictionWindows` needs. */
export interface RankedEvent extends TimestampedEvent {
  importance: 'high' | 'medium' | 'low';
}

/**
 * How confident we are that an event is a genuine prop-firm red folder.
 *
 *   tier1   — Financial Juice AND ForexFactory both call it high-impact USD.
 *             This is what a Lucid "Tier 1 News Warning" names, verified
 *             against their 2026-09-10 email: both sources and the email agree
 *             on Core PPI m/m and PPI m/m at 08:30 ET.
 *   single  — one source only. NOT dismissed: a red folder that only one
 *             scraper caught is still a red folder, and the breach does not
 *             care which feed saw it. The tier exists to say how sure we are,
 *             never to suppress a window.
 *   unknown — ForexFactory was unreachable, so agreement could not be tested.
 *             Distinct from `single` on purpose: "the other source disagreed"
 *             and "there was no other source" are different facts, and
 *             collapsing them would make a network blip look like a conflict.
 */
export type NewsTier = 'tier1' | 'single' | 'unknown';

/** A calendar event carrying its cross-source confidence. */
export interface TieredEvent extends RankedEvent {
  country?: string;
  tier: NewsTier;
}

/**
 * ISO country code Financial Juice uses for the United States.
 *
 * ForexFactory says "USD" for the same releases — a currency, not a country —
 * which is why the two are compared through this constant rather than by
 * equality. Comparing the raw fields finds zero agreement and silently
 * downgrades every event.
 */
export const US_COUNTRY_CODE = 'US';

/**
 * Tag each event with its cross-source confidence.
 *
 * `ffReleases === null` means the ForexFactory fetch failed. Everything is then
 * `unknown` rather than `single`: we have no evidence of disagreement, and
 * reporting one would be inventing a conflict out of a timeout.
 *
 * Only US high-impact events can be tier1, because that is the only set
 * ForexFactory is being asked about. A high-impact UK release is `single` — not
 * because it is doubtful, but because the second source was never consulted
 * about it and cannot corroborate what it was not asked.
 */
export function assignTiers<T extends RankedEvent & { country?: string }>(
  events: T[],
  ffReleases: { timestamp: number }[] | null,
  matches: (releases: { timestamp: number }[], ts: number) => boolean
): (T & { tier: NewsTier })[] {
  return events.map((e) => {
    if (ffReleases === null) return { ...e, tier: 'unknown' as NewsTier };
    const corroborated =
      e.importance === 'high' &&
      e.country === US_COUNTRY_CODE &&
      matches(ffReleases, e.timestamp);
    return { ...e, tier: (corroborated ? 'tier1' : 'single') as NewsTier };
  });
}

/**
 * The red-folder no-trade windows implied by a set of calendar events.
 *
 * RED FOLDER = `importance === 'high'`. Country is deliberately NOT part of
 * this test, matching `useNewsProximity` and the news overlay's default filter.
 * (`utt-engine`'s fair-price anchor uses `country === "US" && high` instead —
 * that is a different rule for a different purpose, not a copy of this one.)
 *
 * OVERLAPS ARE UNIONED, not stacked. Two releases 30 minutes apart are one
 * continuous region you cannot trade through, not two regions that happen to
 * cross; painting them as two would compound the fill and read as "worse here",
 * which is not a distinction the rule makes. Touching windows (one ending
 * exactly where the next begins) merge too — a zero-width gap is not a gap.
 *
 * Returned sorted by `start` and guaranteed disjoint, so a consumer can paint
 * each one exactly once without checking for collisions.
 *
 * Unit-tested in `calendar.test.ts`; the chart-side consumer
 * (`src/components/Chart/CalendarLinePrimitive.ts`) has its own tests.
 */
export function newsRestrictionWindows<T extends RankedEvent>(
  events: T[]
): RestrictionWindow[] {
  const windows = events
    .filter(e => e.importance === 'high' && isUsableTimestamp(e.timestamp))
    .map(e => ({
      start: e.timestamp - NEWS_RESTRICTION_BEFORE_MS,
      end: e.timestamp + NEWS_RESTRICTION_AFTER_MS,
    }))
    .sort((a, b) => a.start - b.start);

  const merged: RestrictionWindow[] = [];
  for (const w of windows) {
    const last = merged[merged.length - 1];
    if (last && w.start <= last.end) {
      last.end = Math.max(last.end, w.end);
    } else {
      merged.push({ ...w });
    }
  }
  return merged;
}
